import React, { useState, useEffect } from 'react';
import {
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Book as BookIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  Notifications as NotificationsIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  CalendarToday as CalendarIcon,
  Announcement as AnnouncementIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import { teacherDashboardService, timetableService } from '../services/apiService';
import { formatDate, getRelativeTime } from '../utils/dateFormat';
import { APP_CONFIG } from '../constants';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 280;

const TeacherDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [todayClasses, setTodayClasses] = useState([]);
  const [loadingTodayClasses, setLoadingTodayClasses] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  // Get today's weekday (1=Monday, 2=Tuesday, ..., 6=Saturday, 7=Sunday)
  const getTodayWeekday = () => {
    const today = new Date();
    const day = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    // Convert to 1=Monday, 2=Tuesday, ..., 6=Saturday, 7=Sunday
    return day === 0 ? 6 : day;
  };

  // Convert 24-hour time to 12-hour format with AM/PM
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  useEffect(() => {
    fetchDashboardData();
    fetchTodayClasses();
  }, [user?.id]);

  const fetchTodayClasses = async () => {
    if (!user?.id) return;
    
    try {
      setLoadingTodayClasses(true);
      const todayWeekday = getTodayWeekday();
      const response = await timetableService.getAll({ day_of_week: todayWeekday });
      const entries = response.data || response || [];
      
      // Filter entries where the teacher_id matches the logged-in teacher
      const teacherClasses = entries.filter(entry => entry.teacher_id === user.id);
      
      // Group by class and period, then sort by start_time
      const grouped = {};
      teacherClasses.forEach(entry => {
        const key = `${entry.class_id}_${entry.period_id}`;
        if (!grouped[key]) {
          grouped[key] = {
            class_id: entry.class_id,
            class_name: entry.class_name,
            class_section: entry.class_section,
            period_id: entry.period_id,
            period_name: entry.period_name,
            start_time: entry.start_time,
            end_time: entry.end_time,
            subject_id: entry.subject_id,
            subject_name: entry.subject_name,
          };
        }
      });
      
      // Convert to array and sort by start_time
      const sortedClasses = Object.values(grouped).sort((a, b) => {
        const timeA = a.start_time || '';
        const timeB = b.start_time || '';
        return timeA.localeCompare(timeB);
      });
      
      setTodayClasses(sortedClasses);
    } catch (err) {
      console.error('Error fetching today\'s classes:', err);
      setTodayClasses([]);
    } finally {
      setLoadingTodayClasses(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await teacherDashboardService.get();
      if (response.success) {
        setDashboardData(response.data);
      } else {
        setError(response.error || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      console.error('Error fetching teacher dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const stats = dashboardData?.stats || {};
  const mySubjects = dashboardData?.my_subjects || [];
  const upcomingActivities = dashboardData?.upcoming_activities || [];
  const announcements = dashboardData?.announcements || [];
  const leaveAllocation = dashboardData?.leave_allocation || null;
  const unreadNotifications = dashboardData?.unread_notifications || 0;

  const statCards = [

    {
      title: 'Attendance of Total School',
      value: `${stats.attendance_today || 0}/${stats.total_active_students || 0}`,
      icon: <CheckCircleIcon />,
      bgColor: '#1976d2', // Orange
      subtitle: stats.total_active_students > 0 
        ? stats.students_present_today + ' students are present'
        : '',
    },
    {
      title: 'My Total Active Class',
      value: stats.total_classes || 0,
      icon: <SchoolIcon />,
      bgColor: '#f57c00', // Blue
    },
    {
      title: 'Total assigned Subjects',
      value: stats.total_subjects || 0,
      icon: <BookIcon />,
      bgColor: '#9c27b0', // Purple
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          drawerWidth={drawerWidth}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            ml: { xs: 0, md: `${0}px` },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          drawerWidth={drawerWidth}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            ml: { xs: 0, md: `${0}px` },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mt: '64px' }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        drawerWidth={drawerWidth}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          // On mobile: always full width, no margin (sidebar overlays)
          // On desktop: adjust width and margin based on sidebar state
          ml: { xs: 0, md: `${0}px` },
          backgroundColor: 'background.default',
        }}
      >
        <Header onMenuClick={handleDrawerToggle} />
        <Box
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            background: (theme) => `linear-gradient(180deg, ${theme.palette.grey[50]}, ${theme.palette.background.default})`,
          }}
        >
          {/* Statistics Cards */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: 4 }}>
            {statCards.map((stat, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: 'none',
                    background: `linear-gradient(135deg, ${stat.bgColor}15 0%, ${stat.bgColor}08 100%)`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${stat.bgColor}, ${stat.bgColor}80)`,
                    },
                    '&:hover': { 
                      boxShadow: `0 8px 30px ${stat.bgColor}40`, 
                      transform: 'translateY(-4px) scale(1.02)',
                      background: `linear-gradient(135deg, ${stat.bgColor}20 0%, ${stat.bgColor}12 100%)`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 3, md: 3.5 }, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {/* Icon at top */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, mb: { xs: 1.5, sm: 2 } }}>
                        <Box
                          sx={{
                            width: { xs: 32, sm: 44, md: 48 },
                            height: { xs: 32, sm: 44, md: 48 },
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${stat.bgColor}, ${stat.bgColor}DD)`,
                            color: 'white',
                            boxShadow: `0 4px 12px ${stat.bgColor}40`,
                          }}
                        >
                          <Box sx={{ fontSize: { xs: 18, sm: 24, md: 26 }, display: 'flex', alignItems: 'center', color: 'white' }}>
                            {stat.icon}
                          </Box>
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.9375rem', md: '1rem' },
                            fontWeight: 600,
                            color: stat.bgColor,
                            letterSpacing: '0.3px',
                          }}
                        >
                          {stat.title}
                        </Typography>
                      </Box>
                      {/* Value below */}
                      <Typography
                        variant="h4"
                        component="div"
                        sx={{ 
                          fontSize: { xs: '1.25rem', sm: '2rem', md: '2.25rem' },
                          fontWeight: 800,
                          color: stat.bgColor,
                          lineHeight: 1.2,
                          mb: stat.subtitle ? 0.5 : 0,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      {stat.subtitle && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: '0.65rem', sm: '0.8125rem', md: '0.875rem' },
                            color: 'text.secondary',
                            mt: 0.5,
                          }}
                        >
                          {stat.subtitle}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {/* Left Column */}
            <Grid item xs={12} lg={8}>
              {/* My Subjects */}
              <Paper
                sx={{
                  mb: 3,
                  p: { xs: 2.5, sm: 3, md: 3.5 },
                  borderRadius: 3,
                  border: 'none',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      }}
                    >
                      <BookIcon />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: '1rem', sm: '1rem' },
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Today's Class
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => navigate('/subjects')}
                    variant="outlined"
                  >
                    View All Subjects
                  </Button>
                </Box>
                <Box sx={{ mt: 1 }}>
                  {loadingTodayClasses ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : todayClasses.length > 0 ? (
                    <>
                      {todayClasses.map((classItem, index) => (
                        <Box
                          key={`${classItem.class_id}_${classItem.period_id}`}
                          sx={{
                            p: 2,
                            mb: 1.5,
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  mb: 0.75,
                                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                  color: 'text.primary',
                                }}
                              >
                                {classItem.subject_name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                <SchoolIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  }}
                                >
                                  {classItem.class_name}
                                  {classItem.class_section && ` - ${classItem.class_section}`}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                  }}
                                >
                                  {classItem.start_time && classItem.end_time 
                                    ? `${formatTimeTo12Hour(classItem.start_time)} - ${formatTimeTo12Hour(classItem.end_time)}`
                                    : classItem.period_name || 'Period'}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              label={classItem.period_name || 'Period'}
                              size="small"
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: 20, sm: 24 },
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </>
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <BookIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        No classes scheduled for today
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* Quick Actions */}
              <Paper
                sx={{
                  mb: 3,
                  p: { xs: 2.5, sm: 3, md: 3.5 },
                  borderRadius: 3,
                  border: 'none',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #f57c00, #ffb74d)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(245, 124, 0, 0.3)',
                    }}
                  >
                    <AssignmentIcon />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: '1rem', sm: '1rem' },
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #f57c00, #ffb74d)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Quick Actions
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => navigate('/attendance')}
                      sx={{ 
                        py: 1.5, 
                        textTransform: 'none', 
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': {
                          boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                        },
                      }}
                      color="success"
                    >
                      Mark Attendance
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AssessmentIcon />}
                      onClick={() => navigate('/results/add')}
                      sx={{ 
                        py: 1.5, 
                        textTransform: 'none', 
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': {
                          boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                        },
                      }}
                      color="primary"
                    >
                      Enter Marks
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AssignmentIcon />}
                      onClick={() => navigate('/subjects')}
                      sx={{ 
                        py: 1.5, 
                        textTransform: 'none', 
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': {
                          boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                        },
                      }}
                      color="secondary"
                    >
                      View Subjects
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Leave Allocation */}
              {leaveAllocation && (
                <Paper
                  sx={{
                    p: { xs: 2.5, sm: 3, md: 3.5 },
                    borderRadius: 3,
                    border: 'none',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #0097a7, #4dd0e1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(0, 151, 167, 0.3)',
                      }}
                    >
                      <EventIcon />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: '1rem', sm: '1rem' },
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #0097a7, #4dd0e1)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Leave Balance
                    </Typography>
                  </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {leaveAllocation.total_leaves || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Total Leaves
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                            {leaveAllocation.leaves_taken || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Taken
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                            {leaveAllocation.leave_balance || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Balance
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    {stats.pending_leaves > 0 && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => navigate('/leave')}
                          sx={{ 
                            textTransform: 'none', 
                            fontWeight: 600,
                            borderRadius: 2,
                          }}
                        >
                          View Leave Requests ({stats.pending_leaves})
                        </Button>
                      </Box>
                    )}
                </Paper>
              )}
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} lg={4}>
              {/* Announcements */}
              <Paper
                sx={{
                  p: { xs: 2.5, sm: 3, md: 3.5 },
                  mb: 3,
                  borderRadius: 3,
                  border: 'none',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #ff9800, #ffb74d)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                      }}
                    >
                      <AnnouncementIcon />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: '1rem', sm: '1rem' },
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #ff9800, #ffb74d)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Announcements
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => navigate('/announcements')}
                    variant="outlined"
                  >
                    View All
                  </Button>
                </Box>
                <Box sx={{ mt: 1, maxHeight: 400, overflowY: 'auto' }}>
                  {announcements.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <AnnouncementIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        No announcements
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {announcements.map((a, index) => (
                        <React.Fragment key={a.id}>
                          <Box
                            sx={{
                              p: 2,
                              mb: 1.5,
                              background: 'white',
                              borderRadius: 2,
                              border: '1px solid rgba(0,0,0,0.1)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                            }}
                          >
                            <Box sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              bgcolor: '#ff9800',
                              flexShrink: 0,
                              mt: 1,
                            }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  mb: 0.5,
                                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                }}
                              >
                                {a.title || 'Announcement'}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.secondary',
                                  mb: 1,
                                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                }}
                              >
                                {a.message}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.disabled',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                }}
                              >
                                <AccessTimeIcon sx={{ fontSize: 12 }} />
                                {getRelativeTime(a.created_at || a.sent_at)}
                              </Typography>
                            </Box>
                          </Box>
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </Box>
              </Paper>
              {/* Upcoming Activities */}
              <Paper
                sx={{
                  p: { xs: 2.5, sm: 3, md: 3.5 },
                  borderRadius: 3,
                  border: 'none',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #9c27b0, #ba68c8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)',
                    }}
                  >
                    <EventIcon />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: '1rem', sm: '1rem' },
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #9c27b0, #ba68c8)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Upcoming Events
                  </Typography>
                </Box>
                <Box sx={{ mt: 1, maxHeight: 400, overflowY: 'auto' }}>
                  {upcomingActivities.length > 0 ? (
                    <>
                      {upcomingActivities.map((activity, index) => (
                        <Box
                          key={activity.id}
                          sx={{
                            p: 2,
                            mb: 1.5,
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Avatar
                              sx={{
                                bgcolor: activity.type === 'holiday' ? 'success.main' : 'primary.main',
                                width: { xs: 36, sm: 40 },
                                height: { xs: 36, sm: 40 },
                              }}
                            >
                              <EventIcon />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  mb: 0.75,
                                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                  color: 'text.primary',
                                }}
                              >
                                {activity.name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  }}
                                >
                                  {formatDate(activity.date)}
                                </Typography>
                              </Box>
                              {activity.description && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {activity.description}
                                </Typography>
                              )}
                            </Box>
                            <Chip
                              label={activity.type === 'holiday' ? 'Holiday' : 'Event'}
                              size="small"
                              sx={{
                                bgcolor: activity.type === 'holiday' ? 'success.main' : 'primary.main',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                height: { xs: 20, sm: 24 },
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </>
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <EventIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        No upcoming events
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default TeacherDashboard;

